import React from "react";
import { motion } from "framer-motion";
import { useApp } from "../AppContext";

const CHEF_ARTWORK =
    "https://customer-assets.emergentagent.com/job_gia-milly-eats/artifacts/9rg3om0a_Gemini_Generated_Image_uctyhructyhructy.png";

const Chefs = () => {
    const { t, lang, branding } = useApp();
    const chefName = branding.chef_name || "Milly";
    const chefSubtitle =
        (lang === "es" ? branding.chef_subtitle_es : branding.chef_subtitle_en) || t.chefs.subtitle;
    const chefBio =
        (lang === "es" ? branding.chef_bio_es : branding.chef_bio_en) ||
        t.chefs.chefMillyBio;
    const customChefImage = branding.chef_image_url;
    const cuisineLabel =
        lang === "es"
            ? branding.country_preset?.cuisine_label_es
            : branding.country_preset?.cuisine_label_en;
    const chefImageStyle = customChefImage
        ? {
              backgroundImage: `url(${customChefImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
          }
        : {
              backgroundImage: `url(${CHEF_ARTWORK})`,
              backgroundSize: "200% 200%",
              backgroundPosition: "100% 0%",
              backgroundRepeat: "no-repeat",
          };
    return (
        <section
            id="chefs"
            data-testid="chefs-section"
            className="relative py-24 sm:py-32 bg-earth-100 grain"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="max-w-3xl mb-14">
                    <span className="pill-tag mb-5">{t.chefs.tag}</span>
                    <h2 className="font-serif font-black text-earth-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        {lang === "es" ? `Conoce a Chef ${chefName}` : `Meet Chef ${chefName}`}
                    </h2>
                    <p className="mt-5 text-lg text-earth-700 max-w-2xl">
                        {chefSubtitle}
                    </p>
                </div>

                <div className="grid md:grid-cols-12 gap-10 lg:gap-14 items-center">
                    <motion.div
                        data-testid="chef-card-milly"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.7 }}
                        className="md:col-span-5 relative group"
                    >
                        <div
                            className="relative aspect-[3/4] overflow-hidden rounded-[32px] bg-earth-200 shadow-xl"
                            style={chefImageStyle}
                        >
                            <div className="absolute inset-0 bg-gradient-to-t from-earth-900/80 via-earth-900/10 to-transparent transition-opacity duration-700 group-hover:opacity-90" />
                            {branding.decorations_enabled && (
                                <div className="absolute top-5 right-5 flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-earth-800">
                                    <span
                                        className="w-3 h-2 inline-block rounded-sm"
                                        style={{
                                            background:
                                                "linear-gradient(to bottom, var(--flag-1) 50%, var(--flag-3) 50%)",
                                        }}
                                    />
                                    {cuisineLabel}
                                </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 p-7 text-earth-50">
                                <div
                                    className="font-accent text-3xl -mb-1"
                                    style={{ color: branding.brand_color_primary, opacity: 0.85 }}
                                >
                                    Chef
                                </div>
                                <h3 className="font-serif font-black text-5xl">{chefName}</h3>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="md:col-span-7"
                    >
                        <div
                            className="font-accent text-3xl leading-none mb-3"
                            style={{ color: branding.brand_color_primary }}
                        >
                            {lang === "es" ? "Las manos detrás del sabor" : "The hands behind the flavor"}
                        </div>
                        <p className="text-earth-800 text-lg leading-relaxed whitespace-pre-line">
                            {chefBio || t.chefs.chefMillyBio}
                        </p>
                        {!chefBio && (
                            <p className="text-earth-700 leading-relaxed mt-4">
                                {lang === "es"
                                    ? "Cada plato que sale de su cocina lleva el sazón de generaciones — esa pizca de cariño que solo una abuela dominicana sabe dar. Recetas que viajaron desde Santo Domingo hasta Montego Bay, hechas a mano y servidas con orgullo."
                                    : "Every plate from her kitchen carries the seasoning of generations — that pinch of love only a Dominican grandmother knows how to give. Recipes that traveled from Santo Domingo to Montego Bay, made by hand and served with pride."}
                            </p>
                        )}
                        <div className="mt-7 grid grid-cols-3 gap-4">
                            {[
                                { n: "20+", l: lang === "es" ? "Años cocinando" : "Years cooking" },
                                { n: "100%", l: lang === "es" ? "Hecho a mano" : "Handmade" },
                                { n: "🇩🇴", l: lang === "es" ? "Receta original" : "Original recipe" },
                            ].map((s, i) => (
                                <div key={i} className="bg-white border border-earth-200 rounded-2xl p-4 text-center">
                                    <div className="font-serif font-black text-2xl text-terracotta-600">{s.n}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-earth-600 font-bold mt-1 leading-tight">
                                        {s.l}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Chefs;
